import {
  createCustomerIfNotExist,
  getCustomer,
  getOrderHistory,
  getPendingOrders,
} from '@/api/customer-api';
import { toast } from '@/hooks/use-toast';
import { Customer } from '@/types/customer-type';
import { Participant } from '@/types/fb-type';
import { TOrderWithRelation } from '@/types/order-type';
import { Calendar, Eye, FileText, MapPin, Phone, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

import CustomerEditDialog from '../customers/CustomerEditDialog';
import { Button } from '../ui/button';
import CreateOrderDialog from './CreateOrderDialog';
import { InvoiceDialog } from './InvoiceDialog';
import OrderChatCell from './OrderChatCell';
import { ReceiptDialog } from './ReceiptDialog';
import ViewOrderDialog from './ViewOrderDialog';
import { routes } from '@/routes/route-constants';
import { navigate } from 'wouter/use-browser-location';

interface OrderDetailsProps {
  currentCustomer?: Participant;
  onOpenOrderDialog?: () => void;
  onViewOrderCallBack?: (currentOrder: TOrderWithRelation) => void;
  onConfirmOrderCallBack?: (status: boolean, currentOrder: TOrderWithRelation) => void;
}

export default function OrderDetailsChatPanel({
  currentCustomer,
  onOpenOrderDialog,
  onViewOrderCallBack,
  onConfirmOrderCallBack,
}: OrderDetailsProps) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [pendingOrders, setPendingOrders] = useState<TOrderWithRelation[]>([]);
  const [orderHistory, setOrderHisotry] = useState<TOrderWithRelation[]>([]);
  const [isOpenOrderDialog, setIsOpenOrderDialog] = useState<boolean>(false);
  const [selectedOrderRelation, setSelectedOrderRelation] = useState<TOrderWithRelation | null>(
    null,
  );
  const [isOpenViewOrderDialog, setIsOpenViewOrderDialog] = useState<boolean>(false);
  const [isOpenInvoiceDialog, setIsOpenInvoiceDialog] = useState<boolean>(false);
  const [isOpenReceiptDialog, setIsOpenReceiptDialog] = useState<boolean>(false);
  const [isOpenEditCustomerDialog, setIsOpenEditCustomerDialog] = useState<boolean>(false);
  const [totalOrderHistory, setTotalOrderHistory] = useState<number>(0);

  const getPendingOrdersForCustomer = async (customer: Customer) => {
    const pendingOrdersResponse = await getPendingOrders(customer);
    if (pendingOrdersResponse.success) {
      setPendingOrders(pendingOrdersResponse.data);
    } else {
      toast({
        title: 'Error',
        description: pendingOrdersResponse.message || 'Unexpecte error on getting pending orders.',
        variant: 'destructive',
      });
    }
  };

  const getOrderHistoryForCustomer = async (customer: Customer) => {
    const orderHistoryResponse = await getOrderHistory(customer);
    if (orderHistoryResponse.success) {
      setOrderHisotry(orderHistoryResponse.data.orders);
      setTotalOrderHistory(orderHistoryResponse.data.total);
    } else {
      toast({
        title: 'Error',
        description: orderHistoryResponse.message || 'Unexpecte error on getting history orders.',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (!currentCustomer) {
      return;
    }
    const createCustomer = async () => {
      if (!currentCustomer.id) {
        toast({
          title: 'Error',
          description: `Undefiend customer facebook id ${currentCustomer.id}`,
          variant: 'destructive',
        });
        return;
      }
      const customerResponse = await createCustomerIfNotExist(currentCustomer!);
      if (customerResponse.success) {
        setCustomer(customerResponse.data);
      } else {
        toast({
          title: 'Error',
          description: customerResponse.message || 'Unexpected error for creating  customer',
          variant: 'destructive',
        });
      }
    };
    createCustomer();
  }, [currentCustomer]);

  const onSaveCustomer = async (customerId: string) => {
    const fetchCustomerResponse = await getCustomer(customerId);
    if (fetchCustomerResponse.success) {
      setCustomer(fetchCustomerResponse.data);
    } else {
      toast({
        title: 'Error',
        description: fetchCustomerResponse.message || 'Unexpected error for fetching  customer',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (!customer) return;
    getPendingOrdersForCustomer(customer);
  }, [customer]);
  useEffect(() => {
    if (!customer) return;
    setOrderHisotry([]);
    setTotalOrderHistory(0);
    getOrderHistoryForCustomer(customer);
  }, [customer]);

  const onSubmitOrder = async (success: boolean, orderId?: string | null) => {
    setIsOpenOrderDialog(false);
    if (success && orderId) {
      await getPendingOrdersForCustomer(customer!);
      await getOrderHistoryForCustomer(customer!);
    }
  };
  const onViewOrder = (orderWithRelation: TOrderWithRelation) => {
    setSelectedOrderRelation(orderWithRelation);
    setIsOpenViewOrderDialog(true);
  };
  const onConfirmOrder = (status: boolean, orderWithRelation: TOrderWithRelation) => {
    if (onConfirmOrderCallBack) {
      onConfirmOrderCallBack(status, orderWithRelation);
    }
  };
  const onViewReceipt = (order: TOrderWithRelation) => {
    setSelectedOrderRelation(order);
    setIsOpenReceiptDialog(true);
  };

  if (!customer) {
    return (
      <div className="hidden md:flex flex-col w-80 border-l border-gray-200">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👤</span>
            </div>
            <p className="text-gray-500 text-sm">Select a customer to view details</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col xl:h-full">
      <div className="flex-shrink-0 border-b border-gray-200 px-6 py-3 bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Customer Details</h3>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-blue-100 rounded-full"
              title="View Customer"
              aria-label="View Customer"
              onClick={() => navigate(routes.customer.detail(customer.id))}
            >
              <Eye className="h-4 w-4 text-blue-600" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-blue-100 rounded-full"
              title="Edit Profile"
              aria-label="Edit Profile"
              onClick={() => setIsOpenEditCustomerDialog(true)}
            >
              <FileText className="h-4 w-4 text-blue-600" />
            </Button>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="flex-shrink-0 border-b border-gray-200 px-6 py-5">
        <div className="flex items-center mb-5">
          <div className="mr-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center overflow-hidden">
              {currentCustomer?.profileImageUrl ? (
                <img className="w-14 h-14 object-cover" src={currentCustomer.profileImageUrl} />
              ) : (
                <span className="text-lg font-semibold text-white">{customer.name?.charAt(0)}</span>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-semibold text-gray-900 truncate">{customer.name}</h4>
            <p className="text-xs text-gray-500 flex items-center mt-0.5">
              <Calendar className="w-3 h-3 mr-1" />
              Customer since{' '}
              {typeof customer.createdAt === 'string'
                ? new Date(customer.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric',
                })
                : 'Unknown'}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Phone className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Phone</p>
              <p className="text-sm text-gray-600 break-all">{customer.phone || 'Not provided'}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Shipping Address</p>
              <p className="text-sm text-gray-600 whitespace-pre-line">
                {customer.address || 'Not provided'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Below xl the whole panel scrolls naturally; at xl: fixed pending height + flexible history */}
      <div className="xl:flex-1 xl:overflow-hidden flex flex-col xl:min-h-0">
        <div className="grid grid-rows-[auto,auto,auto,auto] xl:grid-rows-[auto,14rem,auto,1fr] xl:min-h-0 xl:flex-1">
          {/* Pending Header (sticky within its own row via bg) */}
          <div className="px-6 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                Active Orders
                <button
                  className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                  title="Add New Order"
                  onClick={() => setIsOpenOrderDialog(true)}
                >
                  <Plus className="w-4 h-4 text-gray-600" />
                </button>
              </h3>
              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                {pendingOrders.length} orders
              </span>
            </div>
          </div>

          {/* Pending List (fixed height + scrollable at xl, natural height below) */}
          <div className="xl:min-h-0 xl:overflow-y-auto px-6 py-3 border-b border-gray-200">
            {pendingOrders.length > 0 ? (
              <div className="space-y-3">
                {pendingOrders.map((o) => (
                  <OrderChatCell
                    key={o.order?.id ?? Math.random()}
                    orderWithRelation={o}
                    onView={onViewOrder}
                    onConfirm={onConfirmOrder}
                    onViewInvoice={onViewReceipt}
                  />
                ))}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center space-y-3">
                  <span className="text-sm text-gray-500">No Active Order</span>
                  <div>
                    <Button
                      variant="default"
                      onClick={() => setIsOpenOrderDialog(true)}
                      className="bg-gradient-to-r from-blue-600 to-blue-800"
                    >
                      <Plus size={16} className="mr-1" /> Create New Order
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col xl:min-h-0 rounded-lg border border-gray-200 overflow-hidden">
            {/* Header (fixed at top) */}
            <div className="px-6 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">Order History</h3>
                <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                  {totalOrderHistory} orders
                </span>
              </div>
            </div>

            {/* Scrollable list */}
            <div className="xl:flex-1 xl:min-h-0 xl:overflow-y-auto px-6 py-3">
              {orderHistory.length > 0 ? (
                <div className="space-y-3">
                  {orderHistory.map((o) => (
                    <OrderChatCell
                      key={o.order?.id ?? Math.random()}
                      orderWithRelation={o}
                      onView={onViewOrder}
                      onConfirm={onConfirmOrder}
                      onViewInvoice={onViewReceipt}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-500">No Order History</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <CreateOrderDialog
        currentCustomer={customer}
        isOpenning={isOpenOrderDialog}
        onCloseDialog={() => setIsOpenOrderDialog(false)}
        onSubmitOrder={onSubmitOrder}
      />
      <ViewOrderDialog
        data={selectedOrderRelation}
        open={isOpenViewOrderDialog}
        onClose={() => setIsOpenViewOrderDialog(false)}
        onOpenChange={setIsOpenViewOrderDialog}
      />
      <InvoiceDialog
        data={selectedOrderRelation}
        open={isOpenInvoiceDialog}
        onOpenChange={setIsOpenInvoiceDialog}
      />
      <ReceiptDialog
        data={selectedOrderRelation}
        open={isOpenReceiptDialog}
        onOpenChange={setIsOpenReceiptDialog}
      />
      <CustomerEditDialog
        customer={customer!}
        onOpenChange={setIsOpenEditCustomerDialog}
        open={isOpenEditCustomerDialog}
        onSaved={onSaveCustomer}
      ></CustomerEditDialog>
    </div>
  );
}
